import {Content, ResponsiveTable, useBreakPoints} from '@hconnect/uikit'
import {TableFilterItemProps} from '@hconnect/uikit/src/lib/Molecules/Table/Filter/Item/TableFilterItem.types'
import {TableFilterTypeProps} from '@hconnect/uikit/src/lib/Molecules/Table/Filter/TableFilter.enum'
import {TableFilterType} from '@hconnect/uikit/src/lib/Molecules/Table/Filter/TableFilter.types'
import {PageHeader} from '@hm/fundamental'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {Button, Grid, Paper} from '@mui/material'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {useSnackbar} from 'notistack'
import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {useHistory} from 'react-router-dom'

import {useCountriesConfiguration} from '../../common/hooks/useCountriesConfiguration'
import {useQueryParamState} from '../../common/QueryParamStateHook'
import {useTranslation} from '../../hooks/useTranslation'
import {selectLoggedInUserProfile} from '../../modules/LoggedInUser.selectors'

import {useStyles} from './Announcements.styles'
import {AnnouncementsListRows} from './AnnouncementsListRows'
import {deleteAnnouncement, updateAnnouncement} from './common/api'
import {AnnouncementDetails} from './components'
import {useAnnouncementDetails, useAnnouncements} from './hooks/useAnnouncements'
import {useAnnouncementsFilters} from './hooks/useAnnouncementsFilters'

export type AnnouncementsFilter = {
  rowsPerPage: number
  page: number
  sortedByKey: string
  sortedByDirection: 'asc' | 'desc'
  [filterField: string]: string | number | undefined
}

export interface Announcement {
  messageId: string
  countries: string[]
  businessLines: ('CEM' | 'RMC' | 'AGG')[]
  products: string[] // enum over products?
  validFrom: string | Date
  validTo: string | Date
  message: string
  createdOn: string | Date
  modifiedOn: string | Date
  messageType: 'information' | 'warning'
  orgUnitIds: string[]
  author: string
  pillarType?: 'hConnect' | 'hProduce'
  isDeactivated?: boolean
  isActive?: boolean
}

// eslint-disable-next-line complexity
export const Announcements: React.FC = () => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const history = useHistory()
  const {classes} = useStyles()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)
  const isExtraLarge = ['xl'].includes(screenSizes)
  const loggedInUserProfile = useSelector(selectLoggedInUserProfile)
  const loggedInUserId = loggedInUserProfile?.user_id
  const queryClient = useQueryClient()

  const [filter, setFilter] = useQueryParamState<AnnouncementsFilter>({
    rowsPerPage: 10,
    page: 1,
    sortedByKey: 'creationDate',
    sortedByDirection: 'desc'
  })
  const [announcementDetails, setDetails] = useState<Announcement | undefined>(undefined)
  const {data, isFetching, refetch: reloadAnnouncements, error} = useAnnouncements(filter)
  const {data: announcementDetailsData, isLoading: isDetailsLoading} = useAnnouncementDetails(
    announcementDetails?.messageId
  )
  const {data: countries, isFetching: isCountriesFetching} = useCountriesConfiguration()

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setFilter({...filter, page: newPage})
  }
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({...filter, rowsPerPage: parseInt(event.target.value, 10), page: 1})
  }

  const setSortKey = (sortKey: string) => {
    const asc = !(filter.sortedByKey === sortKey && filter.sortedByDirection === 'asc')
    setFilter({
      ...filter,
      sortedByKey: sortKey,
      sortedByDirection: asc ? 'asc' : 'desc'
    })
  }

  useEffect(() => {
    if (announcementDetails && announcementDetailsData !== undefined) {
      setDetails(announcementDetailsData)
    }
  }, [announcementDetailsData])

  const {
    filterInputValues,
    setFilterInputValues,
    handleDatePickerFilterInputValues,
    handleFilterInputValues,
    setRemovedFilter
  } = useAnnouncementsFilters(filter, setFilter)

  const noResultsMessage = error ? t('announcements.errorOnFetching') : t('announcements.noResults')

  const totalPageCount = data?.total || 0

  const handleClose = () => setDetails(undefined)

  const deleteAnnouncementMutation = useMutation(
    deleteAnnouncement(announcementDetails?.messageId),
    {
      onSuccess: () => {
        enqueueSnackbar(t('announcements.form.successDeleteMsg'), {
          anchorOrigin: {vertical: 'top', horizontal: 'center'},
          variant: 'success'
        })
        void queryClient.invalidateQueries({
          queryKey: ['announcements']
        })
      },
      onError: () => {
        enqueueSnackbar(t('announcements.form.errorDeleteMsg'), {
          anchorOrigin: {vertical: 'top', horizontal: 'center'},
          variant: 'error'
        })
      }
    }
  )

  const deactivateAnnouncementMutation = useMutation(
    updateAnnouncement(announcementDetailsData?.messageId),
    {
      onSuccess: (data) => {
        enqueueSnackbar(
          t(`announcements.form.success${data.isDeactivated ? 'Deactivated' : 'Activated'}Msg`),
          {
            anchorOrigin: {vertical: 'top', horizontal: 'center'},
            variant: 'success'
          }
        )
        void queryClient.invalidateQueries({
          queryKey: ['announcements']
        })
      },
      onError: () => {
        enqueueSnackbar(
          t(
            `announcements.form.error${
              announcementDetailsData?.isDeactivated ? 'Activated' : 'Deactivated'
            }Msg`
          ),
          {
            anchorOrigin: {vertical: 'top', horizontal: 'center'},
            variant: 'error'
          }
        )
      }
    }
  )

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>, announcementId: string) => {
    event.preventDefault()
    if (announcementDetails && announcementId) {
      deleteAnnouncementMutation.mutateAsync()
    }
    setDetails(undefined)
  }

  const handleEdit = (event: React.MouseEvent<HTMLButtonElement>, announcement: Announcement) => {
    event.preventDefault()
    history.push(`/announcements/manage/${announcement.messageId}`)
    setDetails(undefined)
  }

  const toggleDeactivate = (
    event: React.MouseEvent<HTMLButtonElement | HTMLLIElement>,
    shouldDeactivate: boolean
  ) => {
    event.preventDefault()
    if (announcementDetails && announcementDetailsData?.messageId) {
      deactivateAnnouncementMutation.mutateAsync({
        ...announcementDetails,
        isDeactivated: shouldDeactivate
      })
    }
  }

  const SnackbarActionButton = () => (
    <Button onClick={() => void reloadAnnouncements()} variant="text" color="primary">
      {t('usersList.retry')}
    </Button>
  )

  if (error) {
    enqueueSnackbar(
      error?.error?.message ? error.error.message : t('announcements.errorOnFetching'),
      {
        action: <SnackbarActionButton />,
        variant: 'error'
      }
    )
  }

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader
          data-cy="page-announcements"
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          aside={
            <Button
              variant="contained"
              data-test-id="create-user-button"
              onClick={() => history.push('/announcements/create')}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                fontWeight: 500,
                border: '1px solid rgba(255, 255, 255, 0.15)',
                marginTop: isMobile ? '12px' : 0
              }}
            >
              <ArrowForwardIcon className={classes.addIcon} fontSize="small" />
              {t('announcements.createNewAnnouncement')}
            </Button>
          }
        >
          {t('announcements.title')}
        </PageHeader>
      </div>

      <Grid
        container
        style={{columnGap: '16px', marginTop: '22px', alignItems: 'stretch'}}
        wrap="nowrap"
      >
        <Grid
          item
          md={announcementDetails && !isMobile ? 8 : 12}
          sm={12}
          xs={12}
          style={{height: '100%'}}
        >
          <Paper elevation={4} style={{padding: isMobile ? '4px 8px' : '26px 36px'}}>
            <ResponsiveTable
              keyField="messageId"
              columns={AnnouncementsListRows({countries})}
              rows={data?.announcements ?? []}
              isRowSelectable={isMobile ? undefined : () => true}
              onSort={(e, sortKey) => setSortKey(sortKey)}
              sortedBy={filter.sortedByKey}
              sortingOrder={filter.sortedByDirection}
              onRowClick={(item: Announcement) => {
                if (item.messageId === announcementDetails?.messageId) {
                  console.log('cooo')
                  setDetails(undefined)
                } else {
                  setDetails(item)
                }
              }}
              emptyMessage={!isFetching && !isCountriesFetching && noResultsMessage}
              loading={isFetching || isCountriesFetching}
              isMobile={isMobile}
              page={filter.page}
              rowsPerPage={filter.rowsPerPage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              onChangePage={handleChangePage}
              count={totalPageCount}
              enableHorizontalScroll={!isExtraLarge}
              mobileRowDetails={
                announcementDetailsData &&
                announcementDetails && (
                  <AnnouncementDetails
                    announcement={announcementDetailsData}
                    t={t}
                    isLoading={isDetailsLoading}
                    countries={countries}
                    handleClose={handleClose}
                    handleDelete={handleDelete}
                    handleEdit={handleEdit}
                    toggleDeactivate={toggleDeactivate}
                  />
                )
              }
              filterListInputValues={filterInputValues}
              setFilterListInputValues={setFilterInputValues}
              onFilterInputChange={(props: TableFilterItemProps) =>
                props.filterDatePickerRangeOptions
                  ? handleDatePickerFilterInputValues(props)
                  : handleFilterInputValues(props)
              }
              onFilterRemove={(filter: TableFilterType) => {
                setRemovedFilter(filter)
                setFilterInputValues(
                  filterInputValues.filter((inputValue) => {
                    return inputValue.filterType === TableFilterTypeProps.DATEPICKER_RANGE
                      ? inputValue.filterDatePickerRangeOptions?.validTo?.filterField !==
                          filter.filterDatePickerRangeOptions?.validTo?.filterField
                      : inputValue.filterField !== filter.filterField
                  })
                )
              }}
              extraFilters={[
                {
                  filterName: 'Message',
                  filterField: 'message',
                  filterType: TableFilterTypeProps.TEXT
                }
              ]}
              enableColumnSelection={true}
              storeFiltersOptions={
                loggedInUserId ? {userId: loggedInUserId, tableName: 'announcements'} : undefined
              }
              tableHeight="70vh"
              stickyHeader={true}
              enablePinning={true}
            />
          </Paper>
        </Grid>
        {!isMobile && announcementDetailsData ? (
          <Grid item md={4} sm={0} xs={0}>
            <Paper elevation={4} style={{padding: '26px 53px 26px 32px', height: '100%'}}>
              <AnnouncementDetails
                announcement={announcementDetailsData}
                author={announcementDetailsData?.author}
                t={t}
                isLoading={isDetailsLoading}
                countries={countries}
                handleClose={handleClose}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                toggleDeactivate={toggleDeactivate}
              />
            </Paper>
          </Grid>
        ) : null}
      </Grid>
    </Content>
  )
}
